import { useCallback, useEffect, useState } from 'react'

import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { LuckyDrawCampaign } from '@model/LuckyDrawDto'

import { useLocallyPersistedState } from '@hooks/useStorage'

import { LuckyDrawModal } from './modals/LuckyDraw.modal'

interface LuckyDrawResponseProps {
  luckyDrawCampaign: LuckyDrawCampaign | undefined
  openLuckyDrawModal(): void
}

export const useLuckyDraw = (): LuckyDrawResponseProps => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const [, setLuckyDrawEligible] = useLocallyPersistedState('luckyDrawEligible', false)

  const [luckyDrawCampaign, setLuckyDrawCampaign] = useState<LuckyDrawCampaign>()
  const { open } = useModal(LuckyDrawModal, {
    campaign: luckyDrawCampaign,
  })

  useEffect(() => {
    checkLuckyDrawEligible()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const fillLuckyCampaign = useCallback(async (): Promise<LuckyDrawCampaign | undefined> => {
    const eligibility = await apiClient.getLuckyDrawCampaign(locale)

    let pickedCampaign: LuckyDrawCampaign | undefined

    for (let i = 0; i < eligibility.length; i++) {
      if (eligibility[i].canGenerateMoreChances || eligibility[i].hasAvailableChancesToUse) {
        pickedCampaign = eligibility[i]
        break
      }
    }

    if (eligibility.length > 0) {
      setLuckyDrawEligible(true)
    }

    setLuckyDrawCampaign(pickedCampaign ?? eligibility[0])

    return pickedCampaign ?? eligibility[0]
  }, [apiClient, locale, setLuckyDrawEligible])

  const checkLuckyDrawEligible = useCallback(async () => {
    await fillLuckyCampaign()
  }, [fillLuckyCampaign])

  const openLuckyDrawModal = useCallback(async () => {
    let campaign = luckyDrawCampaign

    if (!campaign) {
      campaign = await fillLuckyCampaign()
    }

    if (!campaign) {
      return
    }

    open()
  }, [luckyDrawCampaign, fillLuckyCampaign, open])

  return {
    luckyDrawCampaign,
    openLuckyDrawModal,
  }
}
